import {
	createRouter,
	createWebHistory,
	type RouteRecordRaw,
} from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useConfigStore } from '@/stores/config';
import Permissions from '@/core/constants/Permissions';
import BaseHandlerService from '@/core/services/BaseHandlerService';

const routes: Array<RouteRecordRaw> = [
	{
		path: '/',
		redirect: '/dashboard',
		component: () => import('@/layouts/default-layout/DefaultLayout.vue'),
		meta: {
			middleware: 'auth',
		},
		children: [
			{
				path: '/dashboard',
				name: 'dashboard',
				component: () => import('@/views/Dashboard.vue'),
				meta: {
					pageTitle: 'Dashboard',
					breadcrumbs: ['Dashboards'],
				},
			},
			{
				path: '/security',
				name: 'security',
				meta: {
					breadcrumbs: ['Security'],
				},
				children: [
					{
						path: 'users',
						name: 'users',
						component: () => import('@/views/pages/security/Users/users.vue'),
						meta: {
							pageTitle: 'Users',
							permittedRoles: [Permissions.Users.view],
						},
					},
					{
						path: 'roles',
						name: 'rolesSecurity',
						component: () => import('@/views/pages/security/Roles/roles.vue'),
						meta: {
							pageTitle: 'Roles',
							permittedRoles: [Permissions.Roles.view],
						},
					},
					{
						path: 'roles/AddEditRole/:roleId?',
						name: 'editRole',
						component: () =>
							import('@/views/pages/security/Roles/Component/addEditRole.vue'),
						meta: {
							pageTitle: 'Roles',
							params: true,
							permittedRoles: [
								Permissions.Roles.view,
								Permissions.Roles.update,
							],
						},
					},
					{
						path: 'roles/AssignUser/:roleId?',
						name: 'AssignUser',
						component: () =>
							import('@/views/pages/security/Roles/Component/assignUser.vue'),
						meta: {
							pageTitle: 'Assign Users',
							params: true,
							permittedRoles: [Permissions.Roles.add, Permissions.Roles.update],
						},
					},
				],
			},
			{
				path: '/Integrations',
				name: 'Integrations',
				meta: {
					breadcrumbs: ['Integrations'],
				},
				children: [
					{
						path: 'IntegratedCustomers',
						name: 'IntegratedCustomers',
						component: () =>
							import(
								'@/views/pages/integrations/Customers/integratedCustomers.vue'
							),
						meta: {
							pageTitle: 'Customers',
							permittedRoles: [Permissions.IntegratedCustomers.view],
						},
					},
					{
						path: 'IntegratedCustomers/IntegratedCustomerBranches/:customerId',
						name: 'IntegratedCustomerBranches',
						component: () =>
							import(
								'@/views/pages/integrations/Customers/Component/IntegratedCustomerBranches.vue'
							),
						meta: {
							pageTitle: 'Customer Branches',
							permittedRoles: [Permissions.IntegratedCustomerBranches.view],
						},
					},
					{
						path: 'Deliverect',
						name: 'Deliverect',
						component: () => import('@/views/pages/Deliverect/Deliverect.vue'),
						meta: {
							pageTitle: 'Deliverect',
							permittedRoles: [Permissions.Deliverect.view],
						},
					},
					{
						path: 'deliverect/Branches/:storeId?',
						name: 'DeliverectBranches',
						component: () =>
							import('@/views/pages/DeliverectBranches/DeliverectBranches.vue'),
						meta: {
							pageTitle: 'Integrated Branches',
							params: true,
							permittedRoles: [Permissions.Deliverect.view],
							breadcrumbs: ['Integrated Branches'],
						},
					},
					{
						path: 'Foodics',
						name: 'Foodics',
						component: () => import('@/views/pages/Foodics/Foodics.vue'),
						meta: {
							pageTitle: 'Foodics',
							permittedRoles: [Permissions.Foodics.view],
						},
					},
					{
						path: 'Salla',
						name: 'Salla',
						component: () => import('@/views/pages/Salla/Salla.vue'),
						meta: {
							pageTitle: 'Salla',
							permittedRoles: [Permissions.Salla.view],
						},
					},
					{
						path: 'FoodicsFailedOrders',
						name: 'FoodicsFailedOrders',
						component: () =>
							import(
								'@/views/pages/FoodicsFailedOrders/FoodicsFailedOrders.vue'
							),
						meta: {
							pageTitle: 'Foodics Failed Orders',
							permittedRoles: [Permissions.Foodics.log],
						},
					},
					{
						path: 'foodics/Branches/:storeId?',
						name: 'FoodicsBranches',
						component: () =>
							import('@/views/pages/FoodicsBranches/FoodicsBranch.vue'),
						meta: {
							pageTitle: 'Branches',
							params: true,
							permittedRoles: [Permissions.Foodics.view],
							breadcrumbs: ['Branches'],
						},
					},
					{
						path: 'salla/Branches/:storeId?',
						name: 'SallaBranches',
						component: () =>
							import('@/views/pages/SallaBranches/SallaBranch.vue'),
						meta: {
							pageTitle: 'Branches',
							params: true,
							permittedRoles: [Permissions.Salla.view],
							breadcrumbs: ['Branches'],
						},
					},
				],
			},
			{
				path: '/settings',
				name: 'settings',
				meta: {
					breadcrumbs: ['Settings'],
				},
				children: [
					{
						path: '/cities',
						name: 'cities',
						component: () => import('@/views/pages/Cities/Cities.vue'),
						meta: {
							pageTitle: 'Cities',
							permittedRoles: [Permissions.Cities.view],
							breadcrumbs: ['Cities'],
						},
					},
					{
						path: 'roles/AddEditCity/:cityId?',
						name: 'editCity',
						component: () =>
							import('@/views/pages/Cities/Component/addEditCity.vue'),
						meta: {
							pageTitle: 'Cities',
							params: true,
							permittedRoles: [
								Permissions.Cities.view,
								Permissions.Cities.update,
							],
							breadcrumbs: ['Cities'],
						},
					},
					{
						path: '/citiesCycle',
						name: 'citiesCycle',
						component: () => import('../views/pages/CitiesCycle/Cycles.vue'),
						meta: {
							pageTitle: 'Cities Cycle',
							permittedRoles: [Permissions.CitiesCycle.view],
							breadcrumbs: ['CitiesCycle'],
						},
					},
					{
						path: 'CitiesCycle/AddEditCitiesCycle/:cityCycleId?',
						name: 'editCityCycle',
						component: () =>
							import(
								'../views/pages/CitiesCycle/Component/addEditCityCycle.vue'
							),
						meta: {
							pageTitle: 'Cities Cycle',
							params: true,
							permittedRoles: [
								Permissions.CitiesCycle.view,
								Permissions.CitiesCycle.update,
							],
							breadcrumbs: ['CitiesCycle'],
						},
					},
					{
						path: '/cyclePriority',
						name: 'cyclePriority',
						component: () =>
							import('../views/pages/cyclesPriority/CyclePriority.vue'),
						meta: {
							pageTitle: 'Priority Cycle',
							permittedRoles: [Permissions.CyclesPripority.view],
							breadcrumbs: ['PriorityCycle'],
						},
					},
					{
						path: 'cyclePriority/addEditCyclePriority/:cyclePriorityId?',
						name: 'addEditCyclePriority',
						component: () =>
							import(
								'../views/pages/cyclesPriority/Component/AddEditCyclePriority.vue'
							),
						meta: {
							pageTitle: 'Priority Cycle',
							params: true,
							permittedRoles: [
								Permissions.CyclesPripority.view,
								Permissions.CyclesPripority.update,
							],
							breadcrumbs: ['PriorityCycle'],
						},
					},
					{
						path: '/districts',
						name: 'districts',
						component: () => import('@/views/pages/Districts/Districts.vue'),
						meta: {
							pageTitle: 'Districts',
							permittedRoles: [Permissions.Districts.view],
							breadcrumbs: ['Districts'],
						},
					},
					{
						path: 'Reasons',
						name: 'Reasons',
						children: [
							{
								path: 'InactiveReasons',
								name: 'InactiveReasons',

								component: () =>
									import(
										'@/views/pages/settings/Reasons/InactiveReasons/inactiveReason.vue'
									),
								meta: {
									breadcrumbs: ['Inactive Reasons'],
									pageTitle: 'Inactive Reasons',
									permittedRoles: [Permissions.InactiveReasons.view],
								},
							},
							{
								path: 'EndContractReasons',
								name: 'EndContractReasons',

								component: () =>
									import(
										'@/views/pages/settings/Reasons/EndContractReasons/endContractReason.vue'
									),
								meta: {
									breadcrumbs: ['End Contract Reasons'],
									pageTitle: 'End Contract Reasons',
									permittedRoles: [Permissions.EndContractReasons.view],
								},
							},
							{
								path: 'CancellationReasons',
								name: 'CancellationReasons',
								component: () =>
									import(
										'@/views/pages/CancellationReasons/CancellationReason.vue'
									),
								meta: {
									breadcrumbs: ['Cancellation Reasons'],
									pageTitle: 'Cancellation Reasons',
									permittedRoles: [Permissions.CancellationReasons.view],
								},
							},
							{
								path: 'FailedOrderReasons',
								name: 'FailedOrderReasons',

								component: () =>
									import(
										'@/views/pages/FailedOrderReasons/FailedOrderReason.vue'
									),
								meta: {
									breadcrumbs: ['Failed Order Reasons'],
									pageTitle: 'Failed Order Reasons',
									permittedRoles: [Permissions.FailedOrderReasons.view],
								},
							},
							{
								path: 'CreateFailedOrderReason/:id?',
								name: 'CreateFailedOrderReason',

								component: () =>
									import(
										'@/views/pages/FailedOrderReasons/Component/CreateFailedOrderReason.vue'
									),
								meta: {
									breadcrumbs: ['Create Failed Order Reasons'],
									pageTitle: 'Create Failed Order Reasons',
									permittedRoles: [Permissions.FailedOrderReasons.create],
								},
							},
							{
								path: 'FineReason',
								name: 'FineReasons',
								component: () =>
									import('@/views/pages/fineReasons/FineReason.vue'),
								meta: {
									pageTitle: 'Fine Reasons',
								},
							},
							{
								path: 'ForceCompleteReasons',
								name: 'ForceCompleteReasons',
								component: () =>
									import(
										'@/views/pages/forceCompleteReasons/ForceCompleteReason.vue'
									),
								meta: {
									pageTitle: 'Force Complete Reasons',
								},
							},
						],
					},
					{
						path: '/vehicleTypes',
						name: 'vehicleTypes',
						component: () =>
							import('@/views/pages/vehicleTypes/VehicleTypes.vue'),
						meta: {
							pageTitle: 'Vehicle Types',
							permittedRoles: [Permissions.VehicleTypes.view],
							breadcrumbs: ['Vehicle Types'],
						},
					},
					{
						path: '/OperationWorkingHours',
						name: 'OperationWorkingHours',
						component: () =>
							import(
								'@/views/pages/OperationWorkingHours/OperationWorkingHours.vue'
							),
						meta: {
							pageTitle: 'Operation Working Hours',
							permittedRoles: [Permissions.OperationsWorkingHours.view],
							breadcrumbs: ['Operation Working Hours'],
						},
					},
				],
			},
			{
				path: '/WebsiteSettings',
				name: 'WebsiteSettings',
				meta: {
					breadcrumbs: ['Website Settings'],
				},
				children: [
					{
						path: 'partnerLogos',
						name: 'partnerLogos',
						component: () =>
							import(
								'@/views/pages/WebsiteSettings/PartnerLogos/partnerLogos.vue'
							),
						meta: {
							pageTitle: 'Partner Logos',
							permittedRoles: [Permissions.WebsitePartnerLogos.view],
							breadcrumbs: ['Partner Logos'],
						},
					},
				],
			},
			{
				path: '/MyProfile',
				name: 'MyProfile',
				component: () => import('@/views/pages/profile/MyProfile.vue'),
				meta: {
					pageTitle: 'My Profile',
				},
			},
			{
				path: '/GeneralSettings',
				name: 'GeneralSettings',
				component: () =>
					import('@/views/pages/generalSettings/GeneralSettings.vue'),
				meta: {
					pageTitle: 'General Settings',
					breadcrumbs: ['General Settings'],
					permittedRoles: [Permissions.GeneralSettings.view],
				},
			},
			{
				path: '/Partners',
				name: 'Partners',
				meta: {
					breadcrumbs: ['Partners'],
				},
				children: [
					{
						path: 'Stores',
						name: 'Stores',
						component: () => import('@/views/pages/stores/Stores.vue'),
						meta: {
							pageTitle: 'Stores',
						},
					},
					{
						path: 'Stores/StoreCreation/:storeId?',
						name: 'StoreCreation',
						component: () => import('@/views/pages/stores/StoreCreation.vue'),
						meta: {
							pageTitle: 'Store Creation',
						},
					},
					{
						path: 'Stores/BranchCreation/:storeId/:branchId?/:integrationBranchId?/:integrationTypeId?',
						name: 'BranchCreation',
						component: () =>
							import('@/views/pages/branches/BranchCreation.vue'),
						meta: {
							pageTitle: 'Branch Creation',
						},
						beforeEnter: (to, from, next) => {
							if (
								from.name === 'StoreCreation' ||
								from.name === 'FoodicsBranches' ||
								from.name === 'SallaBranches'
							)
								next();
							else next({ name: 'StoreCreation' });
						},
					},
					{
						path: 'Stores/ContractCreation/:storeId/:contractId?',
						name: 'ContractCreation',
						component: () =>
							import('@/views/pages/contracts/ContractCreation.vue'),
						meta: {
							pageTitle: 'Contract Creation',
						},
						beforeEnter: (to, from, next) => {
							if (from.name === 'StoreCreation') next();
							else next({ name: 'StoreCreation' });
						},
					},
					{
						path: 'StoreChannels',
						name: 'StoreChannels',
						component: () =>
							import('@/views/pages/StoreChannels/StoreChannels.vue'),
						meta: {
							pageTitle: 'Store Channels',
						},
					},
					{
						path: 'Roles',
						name: 'Roles',
						component: () => import('@/views/pages/PortalRoles/Roles.vue'),
						meta: {
							pageTitle: 'Portal Roles',
							permittedRoles: [Permissions.PartnerRoles.view],
						},
					},
					{
						path: 'Roles/RoleCreation',
						name: 'RoleCreation',
						component: () =>
							import('@/views/pages/PortalRoles/RoleCreation.vue'),
						meta: {
							pageTitle: 'Role Creation',
							params: true,
							permittedRoles: [
								Permissions.PartnerRoles.add,
								Permissions.PartnerRoles.update,
							],
						},
						beforeEnter: (to, from, next) => {
							if (from.name === 'Roles') next();
							else next({ name: 'Roles' });
						},
					},
					{
						path: 'Users',
						name: 'Users',
						component: () => import('@/views/pages/PortalUsers/Users.vue'),
						meta: {
							pageTitle: 'Portal Users',
						},
					},
					{
						path: 'Users/UserCreation',
						name: 'UserCreation',
						component: () =>
							import('@/views/pages/PortalUsers/UserCreation.vue'),
						meta: {
							pageTitle: 'User Creation',
						},
						beforeEnter: (to, from, next) => {
							if (from.name === 'Users') next();
							else next({ name: 'Users' });
						},
					},
					{
						path: 'FoodAggregators',
						name: 'FoodAggregators',
						component: () =>
							import('@/views/pages/FoodAggregators/FoodAggregator.vue'),
						meta: {
							breadcrumbs: ['Food Aggregators Management'],
							pageTitle: 'Food Aggregators Management',
							permittedRoles: [Permissions.FoodAggregators.view],
						},
					},
					{
						path: 'CustomerLocations',
						name: 'CustomerLocations',
						component: () =>
							import('@/views/pages/CustomerLocations/CustomerLocations.vue'),
						meta: {
							breadcrumbs: ['Customer Locations'],
							pageTitle: 'Customer Locations',
							permittedRoles: [Permissions.CustomerLocations.view],
						},
					},
					{
						path: 'DriverTrackingRequests',
						name: 'DriverTrackingRequests',
						component: () =>
							import(
								'@/views/pages/PartnerDriverTracking/PartnerDriverTracking.vue'
							),
						meta: {
							breadcrumbs: ['Driver Tracking Requests'],
							pageTitle: 'Driver Tracking Requests',
						},
					},
				],
			},
			{
				path: '/Drivers',
				name: 'Drivers',
				meta: {
					breadcrumbs: ['Drivers'],
				},
				children: [
					{
						path: 'Companies',
						name: 'Companies',
						component: () => import('@/views/pages/companies/Companies.vue'),
						meta: {
							pageTitle: 'Companies',
						},
					},
					{
						path: 'Companies/CompanyCreation',
						name: 'CompanyCreation',
						component: () =>
							import('@/views/pages/companies/CompanyCreation.vue'),
						meta: {
							pageTitle: 'Company Creation',
						},
						beforeEnter: (to, from, next) => {
							if (from.name === 'Companies') next();
							else next({ name: 'Companies' });
						},
					},
					{
						path: 'DriversList',
						name: 'DriversList',
						component: () => import('@/views/pages/drivers/Drivers.vue'),
						meta: {
							pageTitle: 'Drivers',
						},
					},
					{
						path: 'Drivers/DriverCreation/:driverId?',
						name: 'DriverCreation',
						component: () => import('@/views/pages/drivers/DriverCreation.vue'),
						meta: {
							pageTitle: 'Driver Creation',
						},
					},
					{
						path: 'DriversGroup',
						name: 'DriversGroup',
						component: () =>
							import('@/views/pages/driversGroup/DriversGroup.vue'),
						meta: {
							pageTitle: 'Drivers Group',
						},
					},
					{
						path: 'DriversGroup/AddEditDriversGroup/:groupId?',
						name: 'AddEditDriversGroup',
						component: () =>
							import(
								'@/views/pages/driversGroup/Components/AddEditDriversGroup.vue'
							),
						meta: {
							pageTitle: 'Drivers Group',
						},
						beforeEnter: (to, from, next) => {
							if (from.name === 'DriversGroup') next();
							else next({ name: 'DriversGroup' });
						},
					},
					{
						path: 'DriversPerformance',
						name: 'DriversPerformance',
						component: () =>
							import('@/views/pages/DriverPerformance/DriverPerformance.vue'),
						meta: {
							pageTitle: 'Drivers Performance',
							breadcrumbs: ['Drivers Performance'],
							permittedRoles: [Permissions.DriversPerformance.view],
						},
					},
					{
						path: 'DriverFine',
						name: 'DriverFine',
						component: () => import('@/views/pages/DriverFines/DriverFine.vue'),
						meta: {
							pageTitle: 'Drivers Fine',
						},
					},
					{
						path: 'Companies/LogActivity/:companyId',
						name: 'LogActivity',
						component: () => import('@/views/pages/companies/LogActivity.vue'),
						meta: {
							pageTitle: 'Log Activity',
						},
					},
					{
						path: 'DriversGroup/LogActivity/:groupId',
						name: 'GroupLogActivity',
						component: () =>
							import('@/views/pages/driversGroup/LogActivity.vue'),
						meta: {
							pageTitle: 'Driver Group Log Activity',
						},
					},
					{
						path: 'DriverProfile/:driverId',
						name: 'DriverProfile',
						component: () =>
							import('@/views/pages/driverProfile/DriverProfile.vue'),
						meta: {
							pageTitle: 'Driver Profile',
							permittedRoles: true,
							breadcrumbs: ['Driver Profile'],
						},
					},
					{
						path: 'DriverProfile/DriverProfileHistory/:profileId/:driverId',
						name: 'DriverProfileHistory',
						component: () =>
							import(
								'@/views/pages/driverProfileHistory/DriverProfileHistory.vue'
							),
						meta: {
							pageTitle: 'Driver Profile History',
							permittedRoles: true,
							breadcrumbs: ['Driver Profile History'],
						},
					},
					{
						path: 'Drivers/LogActivity/:driverId',
						name: 'DriverLogActivity',
						component: () => import('@/views/pages/drivers/LogActivity.vue'),
						meta: {
							pageTitle: 'Driver Log Activity',
						},
					},
					{
						path: 'DriverOffDays',
						name: 'DriverOffDays',
						component: () =>
							import('@/views/pages/driverOffDays/DriverOffDays.vue'),
						meta: {
							pageTitle: 'Driver Off Days',
						},
					},
					{
						path: 'DriverOffDaysLogs',
						name: 'DriverOffDaysLogs',
						component: () =>
							import('@/views/pages/driverOffDays/DriverOffDaysLogs.vue'),
						meta: {
							pageTitle: 'Driver Off Days Logs',
						},
					},
					{
						path: 'DriverWallet/:driverId',
						name: 'DriverWallet',
						component: () =>
							import('@/views/pages/driverWallets/DriverWallet.vue'),
						meta: {
							pageTitle: 'Driver Wallet',
							permittedRoles: [Permissions.DriverWallet.view],
						},
					},
					{
						path: 'ExportDriverWallet',
						name: 'ExportDriverWallet',
						component: () =>
							import('@/views/pages/driverWallets/ExportDriverWallet.vue'),
						meta: {
							pageTitle: 'Export Driver Wallet',
							permittedRoles: [Permissions.DriverWallet.view],
						},
					},
				],
			},
			{
				path: '/Monitoring',
				name: 'Monitoring',
				meta: {
					breadcrumbs: ['Live Monitoring'],
				},
				children: [
					{
						path: 'LiveMonitoring',
						name: 'LiveMonitoring',
						component: () =>
							import('@/views/pages/LiveMonitoring/LiveMonitoring.vue'),
						meta: {
							pageTitle: 'Live Monitoring',
							permittedRoles: [Permissions.LiveMonitoring.view],
						},
					},
					{
						path: 'DriverLiveTracking',
						name: 'DriverLiveTracking',
						component: () =>
							import('@/views/pages/driverLiveTracking/DriverLiveTracking.vue'),
						meta: {
							pageTitle: 'Driver Live Tracking',
							permittedRoles: [Permissions.DriverLiveTracking.view],
						},
					},
					{
						path: 'DriverLiveTracking/DriverZoneLog/:driverId',
						name: 'DriverZoneLog',
						component: () =>
							import(
								'@/views/pages/driverLiveTracking/components/DriverZonesLogActivity.vue'
							),
						meta: {
							pageTitle: 'Driver Zone Log',
							permittedRoles: [Permissions.DriverLiveTracking.zonesLog],
						},
					},
					{
						path: 'DriverLiveTracking/ManualDriverWorkingHours/:driverPerformanceId',
						name: 'ManualDriverWorkingHours',
						component: () =>
							import(
								'@/views/pages/manualDriverWorkingHours/ManualDriverWorkingHours.vue'
							),
						meta: {
							pageTitle: 'Manual Driver Working Hours',
							permittedRoles: [
								Permissions.DriverLiveTracking.hoursLog,
								Permissions.DriversPerformance.logs,
							],
						},
					},
					{
						path: 'Map',
						name: 'Map',
						component: () => import('@/views/pages/Map/Map.vue'),
						meta: {
							pageTitle: 'Map',
							permittedRoles: [Permissions.Map.view],
						},
					},
					{
						path: 'ManualAssign/:orderId',
						name: 'ManualAssign',
						component: () =>
							import('@/views/pages/AssignOrdersMap/AssignOrderMap.vue'),
						meta: {
							pageTitle: 'Manual Order Assign',
							permittedRoles: [Permissions.LiveMonitoring.manualOrderAssign],
						},
					},
					{
						path: 'OrderMap/:orderId',
						name: 'OrderMap',
						component: () => import('@/views/pages/OrderMap/OrderMap.vue'),
						meta: {
							pageTitle: 'Order Request Map',
							permittedRoles: [Permissions.LiveMonitoring.viewOrderMap],
						},
					},
					{
						path: 'TaskOrderLog',
						name: 'TaskOrderLog',
						component: () =>
							import('@/views/pages/taskOrderLog/TaskOrderLog.vue'),
						meta: {
							pageTitle: 'API Logs',
							permittedRoles: [Permissions.APILog.view],
						},
					},
					{
						path: 'LocationMap',
						name: 'LocationMap',
						component: () =>
							import('@/views/pages/locationMap/locationMap.vue'),
						meta: {
							pageTitle: 'Location Map',
							permittedRoles: [
								Permissions.LiveMonitoring.view,
								Permissions.Orders.view,
							],
						},
					},
				],
			},
			{
				path: '/Zones',
				meta: {
					breadcrumbs: ['Zones'],
				},
				children: [
					{
						path: 'Zones',
						name: 'Zones',
						component: () => import('@/views/pages/zones/Zones.vue'),
						meta: {
							pageTitle: 'Zones',
							permittedRoles: [Permissions.Zones.view],
						},
					},
					{
						path: 'ZoneMap/:zoneId?',
						name: 'ZoneMap',
						component: () => import('@/views/pages/zones/ZoneMap.vue'),
						meta: {
							pageTitle: 'Zone Map',
							permittedRoles: [Permissions.Zones.view],
						},
					},
					{
						path: 'zoneList',
						name: 'zoneList',
						component: () => import('@/views/pages/ZoneList/ZoneList.vue'),
						meta: {
							pageTitle: 'Zone List',
							permittedRoles: [Permissions.Zones.view],
						},
					},
					{
						path: 'zoneList/editZone/:zoneId?',
						name: 'editZone',
						component: () =>
							import('@/views/pages/ZoneList/Components/EditZone.vue'),
						meta: {
							pageTitle: 'Edit Zones',
							params: true,
							permittedRoles: [
								Permissions.Zones.view,
								Permissions.Zones.update,
							],
						},
					},
					{
						path: 'ScheduledZones',
						name: 'ScheduledZones',
						component: () =>
							import('@/views/pages/scheduledZones/ScheduledZones.vue'),
						meta: {
							pageTitle: 'Create a schedule',
							permittedRoles: [Permissions.ScheduledZones.view],
						},
					},
					{
						path: 'ScheduledZoneList',
						name: 'Scheduled Zones',
						component: () =>
							import('@/views/pages/ScheduledZonesList/ScheduledZoneList.vue'),
						meta: {
							pageTitle: 'Scheduled Zones',
							permittedRoles: [Permissions.ScheduledZones.view],
						},
					},
					{
						path: 'ScheduledZoneList/EditScheduledZones/:scheduledZoneId',
						name: 'EditScheduledZones',
						component: () =>
							import(
								'@/views/pages/ScheduledZonesList/components/EditScheduledZone.vue'
							),
						meta: {
							pageTitle: 'Edit Scheduled Zones',
							permittedRoles: [Permissions.ScheduledZones.update],
						},
					},
				],
			},
			{
				path: '/AllOrders',
				meta: {
					breadcrumbs: ['All Orders'],
				},
				children: [
					{
						path: 'Orders',
						name: 'Orders',
						component: () => import('@/views/pages/orders/Orders.vue'),
						meta: {
							pageTitle: 'Orders',
							permittedRoles: [Permissions.Orders.view],
						},
					},
					{
						path: 'FailedOrders',
						name: 'FailedOrders',
						component: () =>
							import('@/views/pages/FailedOrders/FailedOrder.vue'),
						meta: {
							pageTitle: 'Failed Orders',
							permittedRoles: [Permissions.FailedOrders.view],
						},
					},
					{
						path: 'FailedOrderCreation/:orderId/:isFromUpdate?',
						name: 'FailedOrderCreation',
						component: () =>
							import(
								'@/views/pages/FailedOrdersCreation/FailedOrderCreation.vue'
							),
						meta: {
							pageTitle: 'Failed Order Creation',
							breadcrumbs: ['Failed Order Creation'],
						},
					},
				],
			},
		],
	},
	{
		path: '/',
		component: () => import('@/layouts/AuthLayout.vue'),
		children: [
			{
				path: '/sign-in',
				name: 'sign-in',
				component: () =>
					import('@/views/crafted/authentication/basic-flow/SignIn.vue'),
				meta: {
					pageTitle: 'Sign In',
				},
			},
			{
				path: '/sign-up',
				name: 'sign-up',
				component: () =>
					import('@/views/crafted/authentication/basic-flow/SignUp.vue'),
				meta: {
					pageTitle: 'Sign Up',
				},
			},
			{
				path: '/password-reset',
				name: 'password-reset',
				component: () =>
					import('@/views/crafted/authentication/basic-flow/PasswordReset.vue'),
				meta: {
					pageTitle: 'Password reset',
				},
			},
			{
				path: '/ChangeDefaultPassword',
				name: 'ChangeDefaultPassword',
				component: () =>
					import(
						'@/views/crafted/authentication/basic-flow/ChangeDefaultPassword.vue'
					),
				meta: {
					pageTitle: 'Change Default Password',
				},
			},
			{
				path: '/ChangeForgotPassword',
				name: 'ChangeForgotPassword',
				component: () =>
					import(
						'@/views/crafted/authentication/basic-flow/ChangeForgotPassword.vue'
					),
				meta: {
					pageTitle: 'Change Forgot Password',
				},
			},
		],
	},
	{
		path: '/',
		component: () => import('@/layouts/SystemLayout.vue'),
		children: [
			{
				// the 404 route, when none of the above matches
				path: '/404',
				name: '404',
				component: () => import('@/views/crafted/authentication/Error404.vue'),
				meta: {
					pageTitle: 'Error 404',
				},
			},
			{
				path: '/500',
				name: '500',
				component: () => import('@/views/crafted/authentication/Error500.vue'),
				meta: {
					pageTitle: 'Error 500',
				},
			},
			{
				path: '/403',
				name: '403',
				component: () => import('@/views/crafted/authentication/Error403.vue'),
				meta: {
					pageTitle: 'Error 403',
				},
			},
			{
				path: '/NoPermissions',
				name: 'NoPermissions',
				component: () =>
					import('@/views/crafted/authentication/NoPermissions.vue'),
				meta: {
					middleware: 'auth',
					pageTitle: 'No Permissions',
				},
			},
		],
	},
	{
		path: '/:pathMatch(.*)*',
		redirect: '/404',
	},
];

const router = createRouter({
	history: createWebHistory(),
	routes,
	scrollBehavior(to) {
		// If the route has a hash, scroll to the section with the specified ID; otherwise, scroll to the top of the page.
		if (to.hash) {
			return {
				el: to.hash,
				top: 80,
				behavior: 'smooth',
			};
		} else {
			return {
				top: 0,
				left: 0,
				behavior: 'smooth',
			};
		}
	},
});

router.beforeEach((to, from, next) => {
	const authStore = useAuthStore();
	const configStore = useConfigStore();

	// current page view title
	document.title = `${to.meta.pageTitle} - ${import.meta.env.VITE_APP_NAME}`;

	// reset config to initial state
	configStore.resetLayoutConfig();

	// verify auth token before each page change
	// authStore.verifyAuth();

	// Get roles of current view
	const roles = to.meta.permittedRoles as Array<string>;

	// before page access check if page requires authentication
	if (!authStore.isAuthenticated && to.meta.middleware == 'auth')
		next({ name: 'sign-in' });
	else if (
		!BaseHandlerService.isUserHavePermissions() &&
		to.meta.middleware == 'auth' &&
		to.name != 'NoPermissions'
	)
		next({ name: 'NoPermissions' });
	else if (
		roles != undefined &&
		roles != null &&
		roles.length > 0 &&
		!BaseHandlerService.RoleMatch(roles)
	)
		next({ name: '403' });
	else if (to.name == 'sign-in' && authStore.isAuthenticated)
		next({ name: 'dashboard' });
	else next();
});

export default router;
